import { Carousel } from 'primereact/carousel';
import { FC, useEffect, useRef, useState } from 'react';
import { SkillResourceModal } from './SkillResourceModal';
import { DataView } from 'primereact/dataview';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import image1 from '../../assets/when-will-i-use-this.svg';
import image2 from '../../assets/vocabulary.svg';
import { DisabledSupportSkillItem } from './DisabledSupportSkillItem';
import { getCustomSkillStatus } from '../../custom-hooks/useCurrentSkillResult';
import { SupportSkills } from '../../models/SupportSkills';
import { SupportSkillRatingDisplay } from '../common/RatingDisplay';
import { useComponentTracking } from '../../hooks/useComponentTracking';
import { Eye } from 'lucide-react';
import { StudentSingleSkillQuery$data } from '../../graphQLOperations/__generated__/StudentSingleSkillQuery.graphql';
import { Tooltip } from 'primereact/tooltip';
import { usePreviewMode } from '../../contexts/PreviewModeContext';

const SUPPORT_SKILLS_TOOLTIP = 'Completing Supporting Skills can help you be prepared to work on this skill';

const skillResources = [
  {
    image: image1,
    title: 'When will I use this?',
    id: 1,
    cssId: 'whenWillIUseThis',
  },
  {
    image: image2,
    title: 'Vocabulary',
    id: 2,
    cssId: 'vocabulary',
  },
];
const responsiveOptions = [
  {
    breakpoint: '800px',
    numVisible: 2,
    numScroll: 1,
  },
  {
    breakpoint: '600px',
    numVisible: 1,
    numScroll: 1,
  },
];

interface Props {
  studentStateInfo: StudentSingleSkillQuery$data['skillStatesByStudentId'];
  highSupportSkills: SupportSkills[];
  midSupportSkills: SupportSkills[];
  previewMode?: boolean;
}

export const AboutTheSkill: FC<Props> = ({ studentStateInfo, highSupportSkills, midSupportSkills }) => {
  const previewMode = usePreviewMode();
  const [resources, setResources] = useState<{ id: number; image: string; title: string }[]>([]);
  const [showVocabulary, setShowVocabulary] = useState(false);
  const [showWhenIuse, setShowWhenIuse] = useState(false);
  useComponentTracking('About The Skill');
  useEffect(() => {
    setResources(skillResources);
  }, []);

  const showModalsHandler = (id: number) => {
    switch (id) {
      case 1:
        setShowWhenIuse(true);
        break;

      case 2:
        setShowVocabulary(true);
        break;

      default:
        break;
    }
  };

  const supportSkillTemplate = (supportSkillItem: {
    skillName: string;
    className: string;
    skillResult: string;
    code: string;
    assessmentScore: number;
    id: string;
  }) => {
    return (
      <div className="nc_support-skill-item">
        <div className="nc_support-skill-content tw-flex tw-items-center">
          {previewMode ? (
            <DisabledSupportSkillItem skillName={supportSkillItem.skillName} />
          ) : (
            <Link to={`${location.origin}/skill/${supportSkillItem.code}`} className="flex-grow-1">
              {supportSkillItem.skillName}
            </Link>
          )}
          {!previewMode &&
            (supportSkillItem.assessmentScore !== null && supportSkillItem.assessmentScore !== undefined ? (
              <SupportSkillRatingDisplay score={supportSkillItem.assessmentScore} />
            ) : (
              <span className={`nc_skill-result-mini tw-ml-8 ${supportSkillItem.className}`}>
                {supportSkillItem.skillResult}
              </span>
            ))}
        </div>
      </div>
    );
  };
  const supportSkillsTooltipRef = useRef<Tooltip>(null);
  const demoDisabledTooltipRef = useRef<Tooltip>(null);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        supportSkillsTooltipRef.current?.hide();
        demoDisabledTooltipRef.current?.hide();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <div className="tw-w-full tw-grid nc_block !tw-p-[24px]">
        <h1 className="tw-flex tw-items-center tw-w-100 tw-p-1">
          <Eye className="tw-mr-4" aria-hidden="true" /> About This Skill
        </h1>
        <h2 className="tw-w-full tw-text-2xl tw-font-normal tw-not-italic">Essential Question</h2>
        <h3 className="tw-w-5/6 tw-ml-[8%] tw-font-normal" role="none">
          {studentStateInfo[0]?.skill?.essentialQuestion}
        </h3>
        <div className="tw-w-full tw-flex tw-gap-2 tw-items-center">
          <h2 className="tw-text-2xl tw-font-normal tw-not-italic">Supporting Skills</h2>
          <Tooltip
            ref={supportSkillsTooltipRef}
            target="#support-skills-info-btn"
            content={SUPPORT_SKILLS_TOOLTIP}
            position="right"
            event="both"
            autoHide={false}
          />
          <Button
            id="support-skills-info-btn"
            icon="nc_icon-nc_info icon-md"
            rounded
            link
            aria-label="Information about supporting skills"
            aria-describedby="support-skills-info-desc"
          />
          <span id="support-skills-info-desc" className="tw-sr-only">
            {SUPPORT_SKILLS_TOOLTIP}
          </span>
        </div>
        {previewMode && (
          <Tooltip
            ref={demoDisabledTooltipRef}
            target=".nc_support-skill-disabled"
            position="top"
            className="nc_demo-disabled-tooltip"
          />
        )}
        <div className="tw-w-full tw-flex tw-flex-col sm:tw-flex-row tw-items-baseline tw-justify-center tw-gap-5">
          <div className="nc_support-skills">
            <h4 className="tw-flex tw-items-center tw-gap-2 tw-font-normal" role="none">
              Essential Skills
            </h4>
            <DataView
              value={highSupportSkills.map(element => {
                const customStatus = getCustomSkillStatus(
                  element.skillSubstate?.id || '',
                  element.skillAssessment?.score,
                );
                return {
                  id: element.skill?.id,
                  skillName: element.skill?.name,
                  className: customStatus.className,
                  skillResult: customStatus.status,
                  code: element.skill?.code,
                  assessmentScore: element.skillAssessment?.score,
                };
              })}
              itemTemplate={supportSkillTemplate}
              id="highRelevanceSupportSkill"
            ></DataView>
          </div>
          <div className="nc_support-skills">
            <h4 className="tw-flex tw-items-center tw-gap-2 tw-font-normal" role="none">
              Helpful Skills
            </h4>
            <DataView
              value={midSupportSkills.map(element => {
                const customStatus = getCustomSkillStatus(
                  element.skillSubstate?.id || '',
                  element.skillAssessment?.score,
                );
                return {
                  id: element.skill?.id,
                  skillName: element.skill?.name,
                  className: customStatus.className,
                  skillResult: customStatus.status,
                  code: element.skill?.code,
                  assessmentScore: element.skillAssessment?.score,
                };
              })}
              itemTemplate={supportSkillTemplate}
              id="mediumRelevanceSupportSkills"
            ></DataView>
          </div>
        </div>

        <h2 className="tw-w-full tw-text-2xl tw-font-normal tw-not-italic">More Skill Information</h2>
        <Carousel
          id="moreSkillInformation"
          className="nc_carousel tw-w-full"
          numVisible={2}
          value={resources}
          responsiveOptions={responsiveOptions}
          itemTemplate={item => (
            <div
              role="button"
              tabIndex={0}
              onClick={() => showModalsHandler(item.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showModalsHandler(item.id);
                }
              }}
              id={item.cssId}
              className="nc_skill-resource tw-flex tw-flex-col tw-justify-start tw-items-center tw-cursor-pointer"
              aria-label={item.title}
            >
              <img className="nc_skill-resource-image" src={item.image} alt={item.title}></img>
              <h3 role="none">{item.title}</h3>
            </div>
          )}
        />
      </div>
      <SkillResourceModal
        header="Vocabulary"
        fieldSets={studentStateInfo[0]?.skill?.vocabularies?.map(item => ({
          id: item?.id ?? '',
          legend: item?.term || '',
          text: item?.definition || '',
        }))}
        show={showVocabulary}
        setShow={setShowVocabulary}
      />
      <SkillResourceModal
        header="When will I use this?"
        fieldSets={studentStateInfo[0]?.skill?.skillStories?.map(item => ({
          id: item?.id ?? '',
          legend: item?.profession || '',
          text: item?.story || '',
        }))}
        show={showWhenIuse}
        setShow={setShowWhenIuse}
      />
    </>
  );
};
